import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { useAuthModalContext } from '../context/AuthModalContext';
import { normalizeApiError } from '../services/apiError';
import {
  CampaignDetails, CampaignIntent, campaignCouponService, captureCampaignIntent,
  campaignIntentSnapshot, clearCampaignIntent, parseCampaignIntent, subscribeCampaignIntent,
} from '../services/campaignCoupon';
import { hasPendingCreditAward } from '../services/signupCreditAward';

import { CreditRewardModal } from './CreditRewardModal';

interface OfferState {
  details: CampaignDetails | null;
  message: string;
  busy: boolean;
  claimed: boolean;
  accountId?: string;
}

const useCouponDetails = (intent: CampaignIntent, accountId: string | undefined,
  setState: React.Dispatch<React.SetStateAction<OfferState>>) => {
  useEffect(() => {
    let cancelled = false;

    campaignCouponService.details(intent.code).then(value => {
      if (!cancelled) setState(previous => ({ ...previous, details: value, message: '',
        accountId, claimed: previous.accountId === accountId && previous.claimed }));
    }).catch(() => {
      if (!cancelled) setState(previous => ({ ...previous, details: null, accountId,
        claimed: false, message: '' }));
    });
    void campaignCouponService.arrival(intent).catch(() => undefined);

    return () => { cancelled = true; };
  }, [intent, accountId, setState]);
};

const useCouponAccount = (accountId: string | undefined) => {
  const ref = useRef(accountId);

  useEffect(() => {
    ref.current = accountId;

    return () => { ref.current = undefined; };
  }, [accountId]);

  return ref;
};

const useCampaignOffer = (intent: CampaignIntent) => {
  const { user, getCurrentUser } = useAuth();
  const [state, setState] = useState<OfferState>({ details: null,
    message: '', busy: false, claimed: false, accountId: user?.id });
  const accountRef = useCouponAccount(user?.id);
  const claiming = useRef(false);

  useCouponDetails(intent, user?.id, setState);

  // eslint-disable-next-line max-statements
  const claim = async () => {
    if (!user || claiming.current) return;
    const accountId = user.id;

    claiming.current = true;
    setState(previous => ({ ...previous, busy: true }));
    try {
      const result = await campaignCouponService.claim(intent.code);

      if (accountRef.current !== accountId) return;
      setState(previous => ({ ...previous, claimed: true,
        message: result.status === 'claimed' ? 'Your credits are ready to use.' : 'These credits are already in your account.' }));
      void getCurrentUser().catch(() => undefined);
    } catch (err) {
      if (accountRef.current !== accountId) return;
      const { status } = normalizeApiError(err);

      if (status === 401 || status === 403 || status === 404) {
        setState(previous => ({ ...previous, details: null, message: '' }));
      } else {
        setState(previous => ({ ...previous, message: 'We couldn’t add your credits. Please try again.' }));
      }
    } finally {
      claiming.current = false;
      setState(previous => ({ ...previous, busy: false }));
    }
  };

  return { ...state, details: state.accountId === user?.id ? state.details : null,
    claimed: state.accountId === user?.id && state.claimed,
    message: state.accountId === user?.id ? state.message : '', user, claim };
};

const useCleanCampaignUrl = (intent: CampaignIntent) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const code = new URLSearchParams(search).get('coupon')?.trim().toUpperCase();

    if (search && (!code || code === intent.code)) navigate(pathname, { replace: true });
  }, [intent.code, pathname, search, navigate]);
};

const useCampaignDialogState = (authenticated: boolean) => {
  const { isOpen: authOpen } = useAuthModalContext();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const dismiss = () => { clearCampaignIntent(); setDismissed(true); };

  return { open: authenticated && !dismissed && !authOpen && !hasPendingCreditAward()
    && !['/login', '/signup'].includes(pathname), dismiss };
};

const CampaignOffer: React.FC<{ intent: CampaignIntent }> = ({ intent }) => {
  const offer = useCampaignOffer(intent);
  const { details, message, busy, claimed, user, claim } = offer;
  const { open, dismiss } = useCampaignDialogState(!!user);

  useCleanCampaignUrl(intent);

  if (!open || !details || (!claimed && (!details.is_active || !details.eligible || details.already_claimed))) {
    return null;
  }

  return (
    <CreditRewardModal open
      title={claimed ? 'Your credits are ready!' : details.campaign_name || 'A little boost for your next idea'}
      busy={busy} status={claimed ? 'Credits added' : 'A gift for you'} credits={details.credits}
      creditDescription={claimed ? 'added to your account' : 'ready for your next project'}
      message={message || 'Add these free credits to your account and bring your next idea to life.'}
      action={busy ? 'Adding credits…' : claimed ? 'Start building' : 'Claim credits'}
      onAction={() => { if (claimed) dismiss(); else void claim(); }} onClose={dismiss} />
  );
};

const useCaptureCampaign = (pathname: string, search: string): void => {
  const initialCapture = useRef(true);

  useEffect(() => {
    captureCampaignIntent(pathname, search, initialCapture.current);
    initialCapture.current = false;
  }, [pathname, search]);
};

export const CampaignCouponHost: React.FC = () => {
  const { pathname, search } = useLocation();
  const { isLoading } = useAuth();
  const snapshot = useSyncExternalStore(subscribeCampaignIntent, campaignIntentSnapshot, () => null);
  const intent = useMemo(() => parseCampaignIntent(snapshot), [snapshot]);

  useCaptureCampaign(pathname, search);

  if (!intent || isLoading || !['/', '/login', '/signup'].includes(pathname)) return null;

  return <CampaignOffer key={intent.event_id} intent={intent} />;
};
